import { Base } from '@studiometa/js-toolkit';
import Review from './Review.js';

/**
 * Reviews
 *
 * Alimente le slider d'avis (cartes shadow-card) à partir d'un widget Judge.me
 * « Cards Carousel » présent dans la section mais masqué (source de données).
 *
 * Le widget est rendu côté client de façon asynchrone : on observe le DOM
 * jusqu'à ce que ses cartes soient disponibles, puis on lit les X premiers avis
 * (note, texte, auteur) pour remplir les cartes `Review`. Les cartes non
 * utilisées sont retirées du slider.
 */
export default class Reviews extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Reviews',
    components: {
      Review,
    },
    refs: ['aggregate', 'aggregateFill', 'aggregateScore', 'aggregateCount', 'aggregateCountGroup'],
    options: {
      count: {
        type: Number,
        default: 6,
      },
    },
  };

  /**
   * Mount component.
   */
  mounted() {
    this.filled = false;
    // Si l'en-tête est déjà visible, c'est qu'elle a été remplie via les
    // réglages manuels : on ne la surcharge pas.
    this.aggregateFilled = !this.$refs.aggregate.hidden;
    this.update();

    // Les widgets Judge.me (@app) sont rendus par l'app de façon asynchrone,
    // APRÈS le montage : on observe toute la section jusqu'à ce que les cartes
    // et la note agrégée soient disponibles.
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.$el, { childList: true, subtree: true });
  }

  /**
   * Clean up on unmount.
   */
  destroyed() {
    this.observer?.disconnect();
  }

  /**
   * Fill the slider and the aggregate header, and stop observing once both are
   * done.
   */
  update() {
    this.fill();
    this.fillAggregate();

    if (this.filled && this.aggregateFilled) {
      this.observer?.disconnect();
    }
  }

  /**
   * Fill the aggregate header (stars, score, count) from a Judge.me « Cards
   * Carousel » widget when no manual value was set in the section settings.
   */
  fillAggregate() {
    if (this.aggregateFilled) {
      return;
    }

    const { average, count } = this.readAggregate();

    if (!average) {
      return;
    }

    this.aggregateFilled = true;
    this.$refs.aggregateFill.style.width = `${(average / 5) * 100}%`;
    this.$refs.aggregateScore.textContent = average.toFixed(2);

    // On n'injecte que le nombre : le libellé (« basé sur … avis ») reste géré
    // en Liquid pour respecter la traduction / le wording du thème.
    if (count) {
      this.$refs.aggregateCount.textContent = count;
      this.$refs.aggregateCountGroup.hidden = false;
    }

    this.$refs.aggregate.hidden = false;
  }

  /**
   * Read the aggregate rating (average + total count).
   *
   * 1. Attribut `data-average-rating` (Preview Badge Judge.me) ;
   * 2. sinon on parse le texte du widget « Cards Carousel » (« … 4.13 ★ (456) … »).
   * @returns {{ average: number, count: number }}
   */
  readAggregate() {
    const badge = document.querySelector('[data-average-rating]');
    const attrAverage = badge && Number.parseFloat(badge.dataset.averageRating);

    if (attrAverage) {
      return {
        average: attrAverage,
        count: Number.parseInt(badge.dataset.numberOfReviews, 10) || 0,
      };
    }

    const source = this.$el.querySelector('.jdgm-cards-carousel');

    if (!source) {
      return { average: 0, count: 0 };
    }

    const raw = source.textContent.replaceAll(/\s+/g, ' ');
    const average = Number.parseFloat((raw.match(/(\d+[.,]\d+)/) || [])[1]?.replace(',', '.'));
    const count = Number.parseInt(
      (raw.match(/\((\d[\d\s,.]*)\)/) || [])[1]?.replaceAll(/\D/g, ''),
      10,
    );

    return { average: average || 0, count: count || 0 };
  }

  /**
   * Real (non-cloned) review cards rendered by Judge.me, looked up fresh each
   * time (the widget may not exist yet at mount).
   * @returns {HTMLElement[]}
   */
  get cards() {
    // `:not(.jdgm-card)` exclut les cartes du widget « Cards Carousel » (utilisé
    // uniquement comme source de la note agrégée) : les avis viennent du legacy.
    return Array.from(
      this.$el.querySelectorAll(
        '.jdgm-carousel-item:not(.jdgm-card):not(.jdgm-clone-before):not(.jdgm-clone-after)',
      ),
    );
  }

  /**
   * Read the latest reviews from the Judge.me widget and populate the slider.
   */
  fill() {
    if (this.filled) {
      return;
    }

    const reviews = this.cards
      .slice(0, this.$options.count)
      .map((card) => this.extract(card))
      .filter((review) => review.content || review.author);

    if (!reviews.length) {
      return;
    }

    this.filled = true;

    reviews.forEach((review, index) => this.$children.Review[index]?.setData(review));

    // Retire les cartes non alimentées (moins d'avis que le nombre demandé).
    this.$children.Review.forEach((review, index) => {
      if (index >= reviews.length) {
        review.$el.closest('.swiper-slide')?.remove();
      }
    });

    // Le slider Swiper a été initialisé avec des cartes vides : on lui demande
    // de se recalculer (largeurs, pagination) après remplissage.
    const swiper = this.$el.querySelector('.swiper')?.swiper;
    swiper?.update();
    swiper?.slideTo(0, 0);
  }

  /**
   * Extract a review from a Judge.me « Reviews Carousel » item.
   *
   * Structure (legacy carousel) : `.jdgm-carousel-item__review-title` (titre),
   * `.jdgm-carousel-item__review-body` (avis), `.jdgm-carousel-item__reviewer-name`
   * (auteur), `.jdgm-carousel-item__product-title` (produit),
   * `.jdgm-carousel-item__timestamp` (date). Replis défensifs sur d'autres
   * variantes de widgets Judge.me.
   * @param {HTMLElement} card
   * @returns {{ rating: number, title: string, content: string, author: string, product: string, date: string }}
   */
  extract(card) {
    const text = (selector) => {
      const el = card.querySelector(selector);
      return el ? el.textContent.replaceAll(/\s+/g, ' ').trim() : '';
    };

    return {
      rating: card.querySelectorAll('.jdgm-star.jdgm--on').length,
      title: text('.jdgm-carousel-item__review-title') || text('[class*="review-title"]'),
      content:
        text('.jdgm-carousel-item__review-body') ||
        text('.jdgm-text') ||
        text('[class*="review-body"], [class*="body"]'),
      author: text('.jdgm-carousel-item__reviewer-name') || text('[class*="reviewer"]'),
      product:
        text('.jdgm-carousel-item__product-title') ||
        text('.jdgm-product-name') ||
        text('[class*="product-title"], [class*="product"]:not([class*="image"])'),
      date: text('.jdgm-carousel-item__timestamp') || text('[class*="timestamp"], time'),
    };
  }
}
